import React from 'react'
import { RENAMED_VOXEL_TYPES, type VoxelMaterial } from '@/voxelTypes'
import type { VoxelReader } from '@/voxelReaders'
import type { Face } from '@/voxelTypes'
import type { Position } from '@/voxelMath'
import { voxelKind, voxelKindTypes } from './kinds'

export interface VoxelComponentProps {
  keys: number[]
  /** Read-only view of the composed base; components only call `get` (plan §3.8). */
  source: VoxelReader
  positions?: Float32Array
  colors?: Uint8Array
  storage?: Uint32Array
  count?: number
  version?: number
  material?: VoxelMaterial
  opacity?: number
  bounds?: Position
}

export type VoxelComponentWithMetadata = React.ComponentType<VoxelComponentProps> & {
  voxelMetadata?: VoxelTypeMetadata
}

export type BrushSizeMode = 'volume' | 'surface' | 'none'

export interface SurfacePlacementPolicy {
  target: 'cell' | 'surface'
  allowedFaces?: 'any' | readonly Face[]
  requireBackingSurface?: boolean
  allowDocumentPlanes?: boolean
}

/**
 * How a volume stroke of this type writes cells (materials-perf-plan §2.3, B2).
 * - `verb`: 'place' overwrites whatever the brush covers; 'fill' only writes empty cells (sand poured
 *   over a castle leaves the castle).
 * - `lumpVoxels` / `lumpCell`: the lumpy shell of the swept brush (B4; recorded here, not yet consumed).
 * - `smearOnSameType`: a stroke that starts on a cell of this same type repaints that type instead of
 *   adding (clay smear, B5 `repaintIf`).
 */
export interface VoxelStrokePolicy {
  verb: 'place' | 'fill'
  lumpVoxels?: number
  lumpCell?: number
  smearOnSameType?: boolean
}

/**
 * Default eraser scope when a stroke starts on a cell of this type (B5, owner decision Q11):
 * 'self' removes only cells of the same canonical type, 'any' removes whatever the brush covers.
 * The eraser option (`ui.tool.eraser.scope`) overrides this when set.
 */
export type DigScope = 'self' | 'any'

export interface VoxelInteractionPolicy {
  brushSize?: BrushSizeMode
  placement?: SurfacePlacementPolicy
  providesPlacementSurface?: boolean
  voxelHitPlacement?: 'adjacent' | 'hit'
  sameTypeExpansionRadius?: number
  sameTypeSeedRadius?: number
  sameTypeExpansionLumpiness?: number
  stroke?: VoxelStrokePolicy
  digScope?: DigScope
  /** Largest brush radius (voxels) a single stroke of this type may use; unbounded when absent. */
  maxRadius?: number
}

// Metadata for voxel types
export interface VoxelTypeMetadata {
  // Whether this voxel type should attach to the surface it's placed on
  usesSurfaceOrientation?: boolean
  // Whether this type is rendered individually (not instanced)
  individuallyRendered?: boolean
  // Whether face selection should prefer the input seed normal directly (vs compute exposure)
  preferSeedFace?: boolean
  // Whether scribbling this type should always constrain to straight (axis-aligned) lines
  forceStraightScribble?: boolean
  // How rectangles should be rasterized for this type
  // 'filled' = fill area; 'outline' = draw edges/perimeter only
  rectanglePlacement?: 'filled' | 'outline'
  // Whether the type supports per-instance color selection
  supportsColor?: boolean
  // Whether connection should enforce 6-connected orthogonal adjacency (bridge diagonals)
  needsOrthogonalConnectivity?: boolean
  // Interaction behavior for editing tools.
  interaction?: VoxelInteractionPolicy
  // Whether this type's renderer output depends on cells of *other* types in the base
  // (e.g. diamond checks the voxel below it for support). Grouping cannot be skipped for
  // a document containing such a type when only indexed/excluded types changed.
  renderReadsNeighbors?: boolean
}

// How each type is drawn, registered by the renderer (src/voxels/index.tsx). What each type is (its
// metadata) is src/voxels/kinds.ts, and needs no registration.
const voxelRegistry = new Map<string, VoxelSpec>()

const VOXEL_TYPE_ALIASES: Readonly<Record<string, string>> = Object.freeze({
  blob_blur: 'blob',
  ...RENAMED_VOXEL_TYPES,
})

export function canonicalVoxelType(type: string): string {
  return VOXEL_TYPE_ALIASES[type] ?? type
}

// Check if a voxel type uses surface orientation
export function usesSurfaceOrientation(type: string): boolean {
  return voxelKind(canonicalVoxelType(type))?.usesSurfaceOrientation ?? false
}

// Check if a voxel type prefers using the seed normal directly for its face
export function preferSeedFace(type: string): boolean {
  return voxelKind(canonicalVoxelType(type))?.preferSeedFace ?? false
}

// Check if a voxel type should constrain scribble strokes to straight (axis-aligned) lines
export function forceStraightScribble(type: string): boolean {
  return voxelKind(canonicalVoxelType(type))?.forceStraightScribble ?? false
}

// Rectangle placement preference for a voxel type
export function rectanglePlacement(type: string): 'filled' | 'outline' {
  return voxelKind(canonicalVoxelType(type))?.rectanglePlacement ?? 'filled'
}

// Whether a voxel type needs orthogonal connectivity (bridge diagonals)
export function needsOrthogonalConnectivity(type: string): boolean {
  return voxelKind(canonicalVoxelType(type))?.needsOrthogonalConnectivity ?? false
}

export function brushSizeMode(type: string): BrushSizeMode {
  return voxelKind(canonicalVoxelType(type))?.interaction?.brushSize ?? 'volume'
}

export function supportsBrushSize(type: string): boolean {
  return brushSizeMode(type) !== 'none'
}

export function placementPolicy(type: string): SurfacePlacementPolicy {
  return voxelKind(canonicalVoxelType(type))?.interaction?.placement ?? { target: 'cell' }
}

export function voxelHitPlacement(type: string): 'adjacent' | 'hit' {
  return voxelKind(canonicalVoxelType(type))?.interaction?.voxelHitPlacement ?? 'adjacent'
}

export function sameTypeExpansionRadius(type: string): number | undefined {
  return voxelKind(canonicalVoxelType(type))?.interaction?.sameTypeExpansionRadius
}

export function sameTypeSeedRadius(type: string): number | undefined {
  return voxelKind(canonicalVoxelType(type))?.interaction?.sameTypeSeedRadius
}

export function sameTypeExpansionLumpiness(type: string): number | undefined {
  return voxelKind(canonicalVoxelType(type))?.interaction?.sameTypeExpansionLumpiness
}

export function strokePolicy(type: string): VoxelStrokePolicy | undefined {
  return voxelKind(canonicalVoxelType(type))?.interaction?.stroke
}

export function digScope(type: string): DigScope {
  return voxelKind(canonicalVoxelType(type))?.interaction?.digScope ?? 'any'
}

/** Per-stroke radius cap for `type`; `Infinity` when the type sets none (the UI's own max still applies). */
export function maxBrushRadius(type: string): number {
  const max = voxelKind(canonicalVoxelType(type))?.interaction?.maxRadius
  return max != null && Number.isFinite(max) ? Math.max(0, max) : Number.POSITIVE_INFINITY
}

export function providesPlacementSurface(type: string | undefined): boolean {
  if (!type) return true
  return voxelKind(canonicalVoxelType(type))?.interaction?.providesPlacementSurface ?? true
}

export function placementFaceAllowed(type: string, face: Face | undefined): boolean {
  if (!face) return false
  const allowed = placementPolicy(type).allowedFaces ?? 'any'
  return allowed === 'any' || allowed.includes(face)
}

export function effectiveScribbleRadius(type: string, radius: number | undefined): number {
  return brushSizeMode(type) === 'none' ? 0.5 : Math.max(0, radius ?? 0)
}

export type VoxelScribbleShape = 'sphere' | 'cube' | 'disc'
export type VoxelScribblePlacement = 'center' | 'onTop'

export function effectiveScribbleShape(type: string, shape: VoxelScribbleShape | undefined): VoxelScribbleShape {
  return brushSizeMode(type) === 'surface' ? 'disc' : (shape ?? 'sphere')
}

export function effectiveScribblePlacement(type: string, placement: VoxelScribblePlacement | undefined): VoxelScribblePlacement {
  return brushSizeMode(type) === 'surface' ? 'center' : (placement ?? 'center')
}

// Get component for a voxel type
export function getVoxelComponent(type: string): React.ComponentType<VoxelComponentProps> | undefined {
  return voxelRegistry.get(canonicalVoxelType(type))?.component
}

// Get full spec for a voxel type
export function getVoxelSpec(type: string): VoxelSpec | undefined {
  return voxelRegistry.get(canonicalVoxelType(type))
}

// Every voxel type Huebo knows (src/voxels/kinds.ts), in palette order
export function getRegisteredTypes(): string[] {
  return voxelKindTypes()
}

// Whether a voxel type supports color selection
export function supportsColor(type: string): boolean {
  return !!voxelKind(canonicalVoxelType(type))?.supportsColor
}

// Whether a voxel type's renderer reads cells of other types from the base map.
// Unknown (unregistered) types are treated conservatively as reading neighbours.
export function rendererReadsNeighbors(type: string): boolean {
  const kind = voxelKind(canonicalVoxelType(type))
  if (!kind) return true
  return kind.renderReadsNeighbors ?? false
}

import { VoxelSpec } from './spec'

export function registerVoxelType(spec: VoxelSpec) {
  voxelRegistry.set(spec.type, spec)
}

export function registerVoxelTypes(...specs: VoxelSpec[]) {
  specs.forEach(registerVoxelType)
}
